import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, ClipboardCheck, FlaskConical, ShieldCheck, Ship, HelpCircle, FileText, ListChecks, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SectionHeading } from '../components/Section';
import { checkpoints, serviceExtra, getServiceBySlug } from '../data/mock';
import { useServiceBySlug } from '../lib/cms';
import SEO, { BreadcrumbLd, ServiceLd } from '../lib/SEO';
import NotFound from './NotFound';

const iconMap = { ClipboardCheck, FlaskConical, ShieldCheck, Ship };

function BulletList({ items }) {
  return (
    <ul className="mt-4 space-y-2.5">
      {items.map((b) => (
        <li key={b} className="flex items-start gap-2.5 text-[0.95rem] text-aqc-ink/85">
          <CheckCircle2 size={16} className="text-aqc-red mt-1 shrink-0" />
          <span>{b}</span>
        </li>
      ))}
    </ul>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const cms = useServiceBySlug(slug);
  const s = cms || getServiceBySlug(slug);
  if (!s) return <NotFound />;
  const Icon = iconMap[s.icon] || ClipboardCheck;
  const extra = serviceExtra[slug] || {};

  return (
    <div className="font-sans-body">
      <SEO title={`${s.title} | Apex Quality Control`} description={s.tagline} ogImage={s.image} />
      <ServiceLd name={s.title} description={s.tagline} image={s.image} />
      <BreadcrumbLd items={[{ name: 'Home', url: '/' }, { name: 'Services', url: '/services' }, { name: s.title, url: `/services/${s.slug}` }]} />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="container-aqc relative py-16 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="eyebrow">Service</span>
            <div className="mt-4 inline-flex h-12 w-12 rounded-full bg-aqc-navy text-white items-center justify-center"><Icon size={20} /></div>
            <h1 className="mt-4 font-serif text-[2.4rem] sm:text-[3rem] leading-tight font-semibold text-aqc-navy">{s.title}</h1>
            <p className="mt-5 text-aqc-muted text-[1.02rem] leading-relaxed max-w-xl">{s.tagline}</p>
            {s.detailIntro && (
              <p className="mt-4 text-aqc-ink/85 leading-relaxed max-w-xl">{s.detailIntro}</p>
            )}
            <div className="mt-7">
              <Link to={`/contact?service=${s.slug}`}>
                <Button className="bg-aqc-navy hover:bg-[hsl(var(--aqc-navy-2))] text-white h-11 px-6">Request a quote for this service</Button>
              </Link>
            </div>
          </div>
          <img src={s.image} alt={s.title} className="rounded-2xl w-full h-[400px] object-cover shadow-[0_30px_60px_-30px_rgba(15,42,71,0.4)]" />
        </div>
      </section>

      {/* Purpose */}
      {extra.purpose && (
        <section className="py-14 bg-aqc-cream">
          <div className="container-aqc max-w-4xl">
            <span className="eyebrow">Purpose of the Service</span>
            <h2 className="mt-3 font-serif text-3xl text-aqc-navy">Why this service exists.</h2>
            <p className="mt-4 text-aqc-ink/85 leading-relaxed">{extra.purpose}</p>
          </div>
        </section>
      )}

      {/* When required + who benefits */}
      {(extra.whenRequired || extra.whoBenefits) && (
        <section className="py-14">
          <div className="container-aqc grid md:grid-cols-2 gap-6">
            {extra.whenRequired && (
              <div className="bg-white border border-aqc-line rounded-2xl p-7">
                <h3 className="font-serif text-xl text-aqc-navy">When this service is required</h3>
                <BulletList items={extra.whenRequired} />
              </div>
            )}
            {extra.whoBenefits && (
              <div className="bg-white border border-aqc-line rounded-2xl p-7">
                <h3 className="font-serif text-xl text-aqc-navy">Who benefits from it</h3>
                <BulletList items={extra.whoBenefits} />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Scope + deliverables (existing content) */}
      <section className="py-14 bg-aqc-cream">
        <div className="container-aqc grid lg:grid-cols-2 gap-12">
          <div>
            <span className="eyebrow">Scope of Work</span>
            <h2 className="mt-3 font-serif text-3xl text-aqc-navy">What we check.</h2>
            <BulletList items={s.bullets || []} />
          </div>
          <div>
            <span className="eyebrow">Typical Deliverables</span>
            <h2 className="mt-3 font-serif text-3xl text-aqc-navy">What you receive.</h2>
            <BulletList items={s.deliverables || []} />
          </div>
        </div>
      </section>

      {/* Process */}
      {extra.process && (
        <section className="py-14">
          <div className="container-aqc">
            <SectionHeading eyebrow="Process & Checkpoints" title="How the work is performed." />
            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {extra.process.map((p, i) => (
                <div key={p.title} className="bg-white border border-aqc-line rounded-2xl p-6">
                  <div className="text-[0.72rem] uppercase tracking-[0.18em] text-aqc-red font-semibold">Step {String(i + 1).padStart(2, '0')}</div>
                  <h3 className="mt-2 font-serif text-lg text-aqc-navy">{p.title}</h3>
                  <p className="mt-1.5 text-sm text-aqc-muted leading-relaxed">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Docs required + reporting */}
      {(extra.clientDocs || extra.reporting) && (
        <section className="py-14 bg-aqc-cream">
          <div className="container-aqc grid md:grid-cols-2 gap-6">
            {extra.clientDocs && (
              <div className="bg-white border border-aqc-line rounded-2xl p-7">
                <div className="flex items-center gap-2"><FileText size={18} className="text-aqc-red" /><h3 className="font-serif text-xl text-aqc-navy">Documents and information we ask for</h3></div>
                <BulletList items={extra.clientDocs} />
              </div>
            )}
            {extra.reporting && (
              <div className="bg-white border border-aqc-line rounded-2xl p-7">
                <div className="flex items-center gap-2"><ListChecks size={18} className="text-aqc-red" /><h3 className="font-serif text-xl text-aqc-navy">Reporting approach</h3></div>
                <p className="mt-4 text-aqc-ink/85 leading-relaxed">{extra.reporting}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Benefits + limitations */}
      {(extra.benefits || extra.limitations) && (
        <section className="py-14">
          <div className="container-aqc grid md:grid-cols-2 gap-6">
            {extra.benefits && (
              <div className="bg-white border border-aqc-line rounded-2xl p-7">
                <h3 className="font-serif text-xl text-aqc-navy">Benefits to the client</h3>
                <BulletList items={extra.benefits} />
              </div>
            )}
            {extra.limitations && (
              <div className="bg-aqc-cream border border-aqc-line rounded-2xl p-7">
                <div className="flex items-center gap-2"><AlertTriangle size={18} className="text-aqc-red" /><h3 className="font-serif text-xl text-aqc-navy">Limitations and important considerations</h3></div>
                <BulletList items={extra.limitations} />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Checkpoints (existing) */}
      <section className="py-14 bg-aqc-cream">
        <div className="container-aqc">
          <SectionHeading title="Where it fits in your production cycle." />
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {checkpoints.map((c) => (
              <div key={c.n} className="bg-white border border-aqc-line rounded-2xl p-6 text-center card-lift">
                <div className="mx-auto h-14 w-14 rounded-full bg-aqc-navy text-white font-serif text-lg flex items-center justify-center">{c.n}</div>
                <h3 className="mt-3 font-serif text-lg text-aqc-navy">{c.title}</h3>
                <p className="mt-1 text-sm text-aqc-muted">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {extra.faq && (
        <section className="py-14">
          <div className="container-aqc max-w-3xl">
            <SectionHeading eyebrow="Frequently Asked" title="Common questions about this service." />
            <div className="mt-10 space-y-3">
              {extra.faq.map((f) => (
                <details key={f.q} className="group bg-white border border-aqc-line rounded-2xl p-5 open:border-aqc-red transition-colors">
                  <summary className="flex items-center gap-3 cursor-pointer font-medium text-aqc-navy list-none">
                    <HelpCircle size={18} className="text-aqc-red" />
                    <span className="flex-1">{f.q}</span>
                    <span className="text-aqc-muted text-sm group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-3 text-sm text-aqc-muted leading-relaxed pl-7">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related services */}
      {extra.related && extra.related.length > 0 && (
        <section className="py-14 bg-aqc-cream">
          <div className="container-aqc">
            <SectionHeading eyebrow="Related Services" title="You may also need…" />
            <div className="mt-10 grid md:grid-cols-3 gap-4">
              {extra.related.map((rslug) => {
                const rel = getServiceBySlug(rslug);
                if (!rel) return null;
                return (
                  <Link key={rslug} to={`/services/${rslug}`} className="bg-white border border-aqc-line rounded-2xl p-6 card-lift">
                    <h3 className="font-serif text-xl text-aqc-navy">{rel.title}</h3>
                    <p className="mt-1.5 text-sm text-aqc-muted line-clamp-2">{rel.tagline}</p>
                    <div className="mt-4 text-[0.72rem] uppercase tracking-[0.14em] text-aqc-red font-semibold flex items-center gap-1">Learn more <ArrowRight size={14} /></div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-16 bg-aqc-navy text-white">
        <div className="container-aqc text-center">
          <h2 className="font-serif text-3xl">Ready to book {s.title}?</h2>
          <p className="mt-3 text-white/75 max-w-xl mx-auto">Send us your product, factory location and target date — a coordinator will follow up within one business day.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to={`/contact?service=${s.slug}`}><Button className="bg-aqc-red hover:brightness-110 text-white h-11 px-6">Request a Quote</Button></Link>
            <Link to="/services"><Button variant="outline" className="h-11 px-6 border-white/30 text-white hover:bg-white hover:text-aqc-navy bg-transparent">See all services <ArrowRight size={16} className="ml-1" /></Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
